import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../../shared/contexts/LanguageContext";
import { useProgressWithAutoRefresh } from "../../../shared/contexts/ProgressContext";
import { useStepRedirect } from "../../../hooks/useStepRedirect";
import ResponsiveFormLayout from "../../../shared/components/layout/ResponsiveFormLayout";
import CooperationLoader from "../../../shared/components/feedback/CooperationLoader";
import { QRCodeCanvas } from "qrcode.react";
import { ClinicApiService, ClinicInfo } from "../api/ClinicApiService";
import QRDoorSignPrint from "../components/QRDoorSignPrint";
import { buildClinicVerifyUrl } from "../../../shared/utils/publicVerifyUrl";

// Raw API enum -> translation key. Without this the backend enum
// ("pending_registration") leaks into the UI verbatim.
const CLINIC_STATUS_LABEL: Record<string, string> = {
  draft: "clinic_status_draft",
  pending_registration: "clinic_status_pending_registration",
  registered: "clinic_status_registered",
  active: "clinic_status_active",
  inactive: "clinic_status_inactive",
  suspended: "clinic_status_suspended",
  closed: "clinic_status_closed",
};

// Governorate enum -> the English display name used as the translation key.
const GOVERNORATE_DISPLAY: Record<string, string> = {
  baghdad: "Baghdad",
  basra: "Basrah",
  nineveh: "Nineveh",
  maysan: "Maysan",
  diyala: "Diyala",
  anbar: "Al Anbar",
  kirkuk: "Kirkuk",
  saladin: "Saladin",
  dohuk: "Duhok",
  erbil: "Erbil",
  sulaymaniyah: "Sulaymaniyah",
  halabja: "Halabja",
  najaf: "Najaf",
  karbala: "Karbala",
  babil: "Babylon",
  wasit: "Wasit",
  qadisiyah: "Al Qadisiyah",
  muthanna: "Al Muthanna",
  dhi_qar: "Dhiqar",
};

const ClinicManagementScreen: React.FC = () => {
  useStepRedirect(); // Handle redirects for active closure requests

  const [loading, setLoading] = useState(true);
  const [clinic, setClinic] = useState<ClinicInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const { progress, isLoading: progressLoading, clinicId } = useProgressWithAutoRefresh();
  const clinicApi = useMemo(() => new ClinicApiService(), []);

  useEffect(() => {
    const fetchClinicData = async () => {
      // Wait for progress data to load
      if (progressLoading) return;

      // No clinic_id - redirect to home
      if (!clinicId) {
        navigate("/home", { replace: true });
        return;
      }

      // Fetch clinic details
      try {
        const result = await clinicApi.getMyClinic();
        if (result.isSuccess && result.data) {
          setClinic(result.data);
        } else {
          setError(result.error || "Failed to load clinic data");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load clinic data");
      } finally {
        setLoading(false);
      }
    };

    fetchClinicData();
  }, [progress, progressLoading, clinicId, navigate, clinicApi]);

  if (loading || progressLoading) {
    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 bg-white flex justify-center items-center">
        <CooperationLoader />
      </div>
    );
  }

  if (error) {
    return (
      <ResponsiveFormLayout
        showLanguageToggle={true}
        icon="⚠️"
        iconSize="w-24 h-24 text-6xl"
        title={t("error")}
        subtitle={error}
        maxHeight="90vh"
        onBack={() => navigate("/home")}
        showBackButton={true}
      >
        <button
          onClick={() => navigate("/home")}
          className="w-full px-6 py-4 rounded-xl bg-[#2E5D7D] text-white text-base font-semibold"
        >
          {t("back_to_home")}
        </button>
      </ResponsiveFormLayout>
    );
  }

  if (!clinic) {
    return null; // Will redirect via useEffect
  }

  // Label and value stay in separate elements with an explicit direction —
  // concatenating them into one Arabic+Latin string makes bidi reordering push
  // the colon to the wrong visual side.
  const InfoRow = ({ label, value }: { label: string; value: string | number | null | undefined }) => {
    if (value === null || value === undefined || value === "") return null;
    return (
      <div className="flex justify-between items-start gap-3 py-2 border-b border-gray-100">
        <span className="text-sm text-gray-600 font-medium shrink-0">{label}</span>
        <span className="text-sm text-gray-900 font-semibold" dir="auto">{value}</span>
      </div>
    );
  };

  const statusKey = CLINIC_STATUS_LABEL[clinic.status];
  const statusLabel = statusKey ? t(statusKey) : clinic.status;
  // Working hours are a morning/evening shift, not a time range. Requests that
  // predate the field declare none, and the row hides itself rather than
  // showing the raw enum or an empty value.
  const workHoursLabel = clinic.clinic_work_hours ? t(clinic.clinic_work_hours) : null;
  const governorateLabel = clinic.governorate
    ? t(GOVERNORATE_DISPLAY[clinic.governorate] || clinic.governorate)
    : null;

  // "active" is what both the registration and onboarding approvals set on the
  // clinic, and it is the same condition the public endpoint checks.
  const isApproved = clinic.status === "active";
  const verifyUrl = buildClinicVerifyUrl(clinic.id);

  return (
    <ResponsiveFormLayout
      showLanguageToggle={true}
      icon="🏥"
      iconSize="w-24 h-24 text-6xl"
      title={clinic.name || t("clinic_management")}
      subtitle={
        <span dir="auto">
          {t("clinic_status")} — {statusLabel}
        </span>
      }
      maxHeight="90vh"
      onBack={() => navigate("/home")}
      showBackButton={true}
    >
      <div className="w-full space-y-6">
        {/* Clinic Status Badge */}
        <div className={`p-4 rounded-xl ${clinic.status === 'active' ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
          <div className="flex items-center justify-center gap-2">
            <span className="text-2xl">{clinic.status === 'active' ? '✅' : '⏳'}</span>
            <span className="font-semibold text-lg" dir="auto">
              {statusLabel}
            </span>
          </div>
        </div>

        {/* Basic Information */}
        <div className="p-5 rounded-xl border border-gray-200 bg-gradient-to-br from-blue-50 to-indigo-50">
          <h3 className={`text-lg font-bold mb-4 text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
            📋 {t("basic_information")}
          </h3>
          <div className="space-y-1 bg-white p-4 rounded-lg">
            <InfoRow label={t("clinic_name")} value={clinic.name} />
            <InfoRow label={t("registration_number")} value={clinic.registration_number} />
            <InfoRow label={t("clinic_opening_license_number")} value={clinic.license_number} />
            <InfoRow label={t("work_hours")} value={workHoursLabel} />
            <InfoRow label={t("phone")} value={clinic.phone_primary} />
            <InfoRow label={t("email")} value={clinic.email} />
          </div>
        </div>

        {/* Location Information */}
        <div className="p-5 rounded-xl border border-gray-200 bg-gradient-to-br from-green-50 to-emerald-50">
          <h3 className={`text-lg font-bold mb-4 text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
            📍 {t("location_information")}
          </h3>
          <div className="space-y-1 bg-white p-4 rounded-lg">
            <InfoRow label={t("governorate")} value={governorateLabel} />
            <InfoRow label={t("neighborhood")} value={clinic.neighborhood} />
            <InfoRow label={t("street")} value={clinic.street} />
            <InfoRow label={t("building_number")} value={clinic.house_number} />
            <InfoRow label={t("nearest_landmark")} value={clinic.nearest_landmark} />
          </div>
        </div>

        {/* License Details */}
        {(clinic.license_issued_date || clinic.license_expiry_date || clinic.opened_date) && (
          <div className="p-5 rounded-xl border border-gray-200 bg-gradient-to-br from-purple-50 to-pink-50">
            <h3 className={`text-lg font-bold mb-4 text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
              ⏰ {t("license_details")}
            </h3>
            <div className="space-y-1 bg-white p-4 rounded-lg">
              <InfoRow
                label={t("license_issued_date")}
                value={clinic.license_issued_date ? new Date(clinic.license_issued_date).toLocaleDateString() : null}
              />
              <InfoRow
                label={t("license_expiry_date")}
                value={clinic.license_expiry_date ? new Date(clinic.license_expiry_date).toLocaleDateString() : null}
              />
              <InfoRow
                label={t("opened_date")}
                value={clinic.opened_date ? new Date(clinic.opened_date).toLocaleDateString() : null}
              />
            </div>
          </div>
        )}

        {/* Registration Details */}
        {(clinic.created_at || clinic.updated_at) && (
          <div className="p-5 rounded-xl border border-gray-200 bg-gradient-to-br from-yellow-50 to-orange-50">
            <h3 className={`text-lg font-bold mb-4 text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
              📅 {t("registration_details")}
            </h3>
            <div className="space-y-1 bg-white p-4 rounded-lg">
              <InfoRow
                label={t("registered_on")}
                value={clinic.created_at ? new Date(clinic.created_at).toLocaleDateString() : null}
              />
              <InfoRow
                label={t("last_updated")}
                value={clinic.updated_at ? new Date(clinic.updated_at).toLocaleDateString() : null}
              />
            </div>
          </div>
        )}

        {/* QR Door Sign — approved clinics only. The backend independently
            refuses to publish anything that is not currently approved, so this
            gate is for clarity, not security. */}
        {isApproved && (
          <div className="p-5 rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50 to-slate-50">
            <h3 className={`text-lg font-bold mb-3 text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
              🔳 {t("qr_door_sign")}
            </h3>
            <p className={`text-sm text-gray-600 mb-4 leading-relaxed ${isRTL ? "text-right" : "text-left"}`}>
              {t("print_qr_door_sign")}
            </p>
            <div className="flex justify-center mb-4 bg-white p-4 rounded-lg border border-gray-200">
              <QRCodeCanvas value={verifyUrl} size={160} level="H" includeMargin />
            </div>
            <button
              onClick={() => window.print()}
              className="w-full min-h-[52px] px-6 py-3 rounded-xl bg-[#2E5D7D] text-white text-base font-semibold shadow-md hover:bg-[#234a5f] hover:shadow-lg active:scale-[0.98] transition-all flex items-center justify-center gap-2"
            >
              <span className="text-xl">🖨️</span>
              {t("print_qr_code")}
            </button>
          </div>
        )}

        {/* Clinic Actions */}
        <div className="space-y-3 pt-4 border-t border-gray-200">
          <h3 className={`text-lg font-bold text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
            ⚙️ {t("clinic_actions")}
          </h3>

          {/* Renew License - Coming Soon */}
          {/* <button
            disabled
            className="w-full px-6 py-4 rounded-xl border-2 border-gray-300 text-gray-400 text-base font-semibold opacity-60 cursor-not-allowed"
          >
            🔄 {t("renew_license")} ({t("coming_soon")})
          </button> */}

          {/* Relocate Clinic - Coming Soon */}
          {/* <button
            disabled
            className="w-full px-6 py-4 rounded-xl border-2 border-gray-300 text-gray-400 text-base font-semibold opacity-60 cursor-not-allowed"
          >
            📍 {t("relocate_clinic")} ({t("coming_soon")})
          </button> */}

          {/* Close Clinic */}
          <button
            onClick={() => navigate("/clinic-closure")}
            className="w-full px-6 py-4 rounded-xl border-2 border-red-500 text-red-500 text-base font-semibold hover:bg-red-500 hover:text-white transition-all"
          >
            ❌ {t("close_clinic") || "Close Clinic"}
          </button>
        </div>
      </div>

      {/* Print-only door sign (portals to body; hidden on screen) */}
      {isApproved && (
        <QRDoorSignPrint
          clinicId={clinic.id}
          clinicName={clinic.name}
          licenseNumber={clinic.license_number}
        />
      )}
    </ResponsiveFormLayout>
  );
};

export default ClinicManagementScreen;
